import { useMutation } from '@tanstack/react-query';
import { handleUnauthorized, isAuthenticated } from '@/common/auth-utils';
import { useRecentApps } from '@/hooks/use-recent-apps';
import { useToast } from '@/hooks/use-toast';
import type { UserApplication } from '@/types/api.types';
import { buildAppLaunchPath } from '@/utils/app-launch-utils';
import { logError } from '@/utils/error-logger';

interface UseApplicationLaunchOptions {
  onSuccess?: () => void;
}

interface LaunchVariables {
  application: UserApplication;
  targetWindow: Window | null;
}

interface UseApplicationLaunchResult {
  loadingAppId: string | null;
  launchApplication: (application: UserApplication) => void;
}

export function useApplicationLaunch(
  options?: UseApplicationLaunchOptions
): UseApplicationLaunchResult {
  const { addRecentApp } = useRecentApps();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: async ({ application, targetWindow }: LaunchVariables) => {
      const authed = await isAuthenticated();

      if (!authed) {
        throw new Error('Unauthorized');
      }

      return { application, targetWindow };
    },
    onSuccess: ({ application, targetWindow }) => {
      const targetUrl = buildAppLaunchPath(application);
      let launched = false;

      if (targetWindow && !targetWindow.closed) {
        targetWindow.opener = null;
        targetWindow.location.href = targetUrl;
        launched = true;
      } else {
        launched =
          window.open(targetUrl, '_blank', 'noopener,noreferrer') !== null;
      }

      if (!launched) {
        logError(
          new Error(`Popup blocked launching ${application.app_id}`),
          'applications:launch:popupBlocked'
        );
        toast.error({
          title: 'Launch blocked',
          message:
            'Your browser blocked the app window. Allow pop-ups for this site and try again.',
        });

        return;
      }

      addRecentApp(application.app_id);
      options?.onSuccess?.();
    },
    onError: (_error, variables) => {
      variables.targetWindow?.close();
      handleUnauthorized();
    },
  });

  const launchApplication = (application: UserApplication) => {
    if (mutation.isPending) {
      return;
    }

    const targetWindow = window.open('about:blank', '_blank');
    mutation.mutate({ application, targetWindow });
  };

  const loadingAppId = mutation.isPending
    ? (mutation.variables?.application.app_id ?? null)
    : null;

  return {
    loadingAppId,
    launchApplication,
  };
}
